import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import styled from "styled-components";
import {
  getHoodies,
  getMyProfile,
  isHoodieApiConfigured,
} from "../../api/services/hoodieApi";
import {
  extractRecommendationListingIds,
  filterHoodieRows,
  formatApiError,
  formatUserLabel,
  normalizeHoodiesResponse,
} from "./hoodieProfileWranglerUtils";
import AdminToolsMlistAuthStatus from "./AdminToolsMlistAuthStatus";

const Wrap = styled.div`
  width: 100%;
  padding: 24px 16px;
`;

const Title = styled.h1`
  font-size: 22px;
  margin: 0;
`;

const Sub = styled.div`
  color: #666;
  margin-top: 4px;
  font-size: 13px;
`;

const Card = styled.div`
  border: 1px solid #e5e5e5;
  border-radius: 10px;
  background: #fff;
  padding: 14px;
  margin-top: 12px;
`;

const Banner = styled.div`
  border-radius: 10px;
  padding: 10px 12px;
  border: 1px solid ${(p) => (p.$kind === "error" ? "#f1b7b7" : "#f0e0a8")};
  background: ${(p) => (p.$kind === "error" ? "#fff5f5" : "#fffbeb")};
  color: ${(p) => (p.$kind === "error" ? "#7a1c1c" : "#6b5700")};
  font-size: 13px;
  margin-top: 12px;
`;

const Controls = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  align-items: end;
  margin-top: 12px;
`;

const Label = styled.label`
  display: flex;
  flex-direction: column;
  gap: 4px;
  font-size: 12px;
`;

const Input = styled.input`
  padding: 8px 10px;
  border: 1px solid #d8d8d8;
  border-radius: 8px;
  min-width: 220px;
`;

const Button = styled.button`
  padding: 9px 12px;
  border-radius: 8px;
  border: 1px solid #d0d0d0;
  background: ${(p) => (p.$primary ? "#111" : "#fff")};
  color: ${(p) => (p.$primary ? "#fff" : "#111")};
  cursor: pointer;
  opacity: ${(p) => (p.disabled ? 0.55 : 1)};
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  margin-top: 10px;
`;

const Th = styled.th`
  text-align: left;
  font-size: 12px;
  color: #666;
  border-bottom: 1px solid #eee;
  padding: 8px;
`;

const Td = styled.td`
  border-bottom: 1px solid #f0f0f0;
  padding: 8px;
  font-size: 14px;
`;

export default function HoodieProfileWranglerListPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const qParam = searchParams.get("q") || "";
  const limitParam = Number(searchParams.get("limit") || "25");
  const offsetParam = Number(searchParams.get("offset") || "0");

  const [q, setQ] = useState(qParam);
  const [limit] = useState(
    Number.isFinite(limitParam) && limitParam > 0 ? limitParam : 25
  );
  const offset =
    Number.isFinite(offsetParam) && offsetParam >= 0 ? offsetParam : 0;

  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);
  const [listBlocked, setListBlocked] = useState(false);
  const [rawData, setRawData] = useState(null);
  const [manualId, setManualId] = useState("");

  const normalized = useMemo(
    () => normalizeHoodiesResponse(rawData),
    [rawData]
  );

  const filtered = useMemo(
    () => filterHoodieRows(normalized.results, qParam),
    [normalized.results, qParam]
  );

  async function loadList() {
    setLoading(true);
    setErr(null);
    setListBlocked(false);
    try {
      const res = await getHoodies({ limit, offset });
      setRawData(res);
    } catch (e) {
      setRawData(null);
      if (e?.status === 403 || e?.status === 401) {
        setListBlocked(true);
      }
      setErr(e);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    if (!isHoodieApiConfigured()) return;
    loadList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [limit, offset]);

  function applyParams(next) {
    const sp = new URLSearchParams(searchParams);
    Object.entries(next).forEach(([k, v]) => {
      if (v === undefined || v === null || v === "") sp.delete(k);
      else sp.set(k, String(v));
    });
    setSearchParams(sp);
  }

  async function openSessionProfile() {
    setLoading(true);
    setErr(null);
    try {
      const me = await getMyProfile();
      if (me?.id != null) {
        navigate(`/admin-tools/hoodies/${me.id}`);
      } else {
        navigate("/admin-tools/hoodies/me");
      }
    } catch (e) {
      setErr(e);
    } finally {
      setLoading(false);
    }
  }

  if (!isHoodieApiConfigured()) {
    return (
      <Wrap>
        <Title>Hoodie / Profile Data Wrangler</Title>
        <Card>
          Set <code>REACT_APP_HOODIE_API_BASE_URL</code> to use the legacy hoodie
          API.
        </Card>
      </Wrap>
    );
  }

  return (
    <Wrap>
      <Title>Hoodie / Profile Data Wrangler</Title>
      <Sub>
        Read-only v1 • Legacy API: <code>/api/hoodie/hoodies/</code>
      </Sub>

      <div style={{ marginTop: 10 }}>
        <Link to="/admin-tools">← Admin Tools</Link>
      </div>

      <AdminToolsMlistAuthStatus />

      {listBlocked ? (
        <Banner $kind="warn">
          <strong>List endpoint not available for this session.</strong> Legacy{" "}
          <code>IsOwnProfileOrUnauthorized</code> only allows{" "}
          <code>GET hoodies/me/</code> and <code>GET hoodies/:id/</code> for your
          own profile — not a full directory list. Use “Open session profile” or
          enter a hoodie id below.
        </Banner>
      ) : null}

      {err && !listBlocked ? (
        <Banner $kind="error">
          {formatApiError(err)}
          {err.status ? ` (HTTP ${err.status})` : ""}
        </Banner>
      ) : null}

      <Card>
        <div style={{ fontWeight: 700, marginBottom: 8 }}>Quick access</div>
        <Controls>
          <Button $primary type="button" disabled={loading} onClick={openSessionProfile}>
            Open session profile (hoodies/me/)
          </Button>
          <Label>
            Hoodie id
            <Input
              value={manualId}
              onChange={(e) => setManualId(e.target.value)}
              placeholder="e.g. 42 or me"
            />
          </Label>
          <Button
            type="button"
            disabled={!manualId.trim()}
            onClick={() =>
              navigate(`/admin-tools/hoodies/${encodeURIComponent(manualId.trim())}`)
            }
          >
            Open by id
          </Button>
        </Controls>
      </Card>

      <Card>
        <div style={{ fontWeight: 700 }}>Directory list (if permitted)</div>
        <Sub style={{ marginTop: 4 }}>
          Backend has no search filter on hoodies. Client-side filter applies after
          load.
        </Sub>
        <Controls as="form"
          onSubmit={(e) => {
            e.preventDefault();
            applyParams({ q, offset: 0 });
          }}
        >
          <Label>
            Filter loaded rows
            <Input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="email, name, zip, id…"
            />
          </Label>
          <Button $primary type="submit" disabled={loading || listBlocked}>
            Apply filter
          </Button>
          <Button type="button" disabled={loading} onClick={() => loadList()}>
            Reload list
          </Button>
        </Controls>

        {loading ? (
          <p>
            <em>Loading…</em>
          </p>
        ) : listBlocked ? (
          <p style={{ color: "#666", fontSize: 13 }}>
            List not loaded — see API gap above.
          </p>
        ) : filtered.length === 0 ? (
          <p style={{ color: "#666" }}>No hoodie profiles in this page.</p>
        ) : (
          <Table>
            <thead>
              <tr>
                <Th>ID</Th>
                <Th>User</Th>
                <Th>Zip</Th>
                <Th>Favorites</Th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((row) => (
                <tr key={row.id}>
                  <Td>
                    <Link to={`/admin-tools/hoodies/${row.id}`}>{row.id}</Link>
                  </Td>
                  <Td>{formatUserLabel(row.user)}</Td>
                  <Td>{row.zip_code ?? "—"}</Td>
                  <Td>
                    {extractRecommendationListingIds(row.recommendations).length}
                  </Td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
      </Card>
    </Wrap>
  );
}
